"use strict";

const diagnostics = require("./expressDiagnostics");
const confirmationForm = require("./expressConfirmationForm");
const expressDom = require("./expressDom");

/**
 * DGF-3054 — handles WP1's express preflight failure shape.
 *
 * WP1's guard rejects an express payment before authorisation and answers
 * { error: true, paymentError: true, cartError: true, redirectUrl: <Cart-Show> }. The vendor wallet
 * classes only test for `resultCode` (googlepay.js:279, applepay.js equivalent); anything without
 * one falls into handleError, which submits #showConfirmationForm with { error: true } and lands
 * the shopper on a generic error instead of back where the problem is.
 *
 * app_adyen_SFRA cannot be modified, so the response is intercepted by wrapping the wallet
 * instance's own callPaymentFromComponent — an instance arrow property, so reassigning it is
 * enough and the class is untouched.
 *
 * Removing #showConfirmationForm before navigating is deliberate: the vendor's fallback path calls
 * document.querySelector('#showConfirmationForm')?.submit(), so deleting the element makes that a
 * no-op and prevents two navigations racing.
 *
 * DGF-4651 adds expressAddressError to the same shape. Unlike the generic rejection, no
 * navigation: the fix lives in the wallet sheet on this page, so the message renders under the
 * buttons and the shopper retries in place.
 *
 * Handling the response is not enough: the vendor onSubmit continuation recreates the confirmation
 * form (applepay.js ~183) or leaves the global spinner running (googlepay.js ~266). So for THIS
 * case the wrapped callPaymentFromComponent throws a sentinel, aborting onSubmit at its await;
 * wrapOnSubmit catches it so it cannot escape as an unhandled rejection (Apple has no try/catch of
 * its own; Google's catch rejects the sheet acceptably first). The DGF-3054 cartError path is
 * untouched - it navigates, making the continuation moot.
 */

/**
 * @param {Object} response - response body from Adyen-PaymentFromComponent
 * @returns {boolean} whether this is a preflight/cart rejection rather than a payment result
 */
function isCartRejection(response) {
    return !!(response && response.cartError === true);
}

/**
 * Handles a preflight rejection.
 *
 * @param {Object} deps - injected dependencies ({ win, doc, $ })
 * @param {string} surface - "cart", "minicart" or "pdp"
 * @param {Object} response - response body from Adyen-PaymentFromComponent
 * @returns {boolean} whether the rejection was handled here
 */
function handleCartRejection(deps, surface, response) {
    if (!isCartRejection(response)) {
        return false;
    }

    diagnostics.record(deps, {
        surface: surface,
        wallet: "*",
        reason: "preflight-cart-error",
        detail: response.redirectUrl || ""
    });

    const doc = deps.doc;
    const existingForm = doc && typeof doc.getElementById === "function"
        ? doc.getElementById(confirmationForm.CONFIRMATION_FORM_ID)
        : null;
    if (existingForm && existingForm.parentNode) {
        existingForm.parentNode.removeChild(existingForm);
    }

    if (response.expressAddressError === true) {
        // distinct reason so this is distinguishable from a generic rejection in the log
        diagnostics.record(deps, {
            surface: surface,
            wallet: "*",
            reason: "preflight-express-address-error",
            detail: response.errorMessage || ""
        });

        try {
            const container = expressDom.findExpressContainers(deps.doc, surface)[0];
            const message = response.errorMessage || deps.errorMessage;
            if (container && message) {
                expressDom.setSurfaceError(container, message);
            }
        } catch (error) {
            // A payment path must never throw from error handling.
        }

        return true;
    }

    const location = deps.win && deps.win.location;
    if (!location) {
        return true;
    }

    if (surface === "pdp") {
        // Cart-Show is the wrong destination from a PDP: the express attempt used a temporary
        // basket that the guard has already discarded, so the shopper's real bag has nothing to do
        // with what they just tried to buy. Reloading the product page puts them back where they
        // were, with fresh availability and a fresh token.
        if (typeof location.reload === "function") {
            location.reload();
        }
        return true;
    }

    if (response.redirectUrl && typeof location.assign === "function") {
        location.assign(response.redirectUrl);
    } else if (typeof location.reload === "function") {
        location.reload();
    }
    return true;
}

// Flag on the sentinel Error thrown once a rejection is fully handled - see the module doc.
const SD_EXPRESS_HANDLED_FLAG = "sdExpressHandled";

/**
 * Stops the wallet's global spinner (Google Pay starts it before the call and only stops it on
 * paths the sentinel bypasses). Defensive: cleanup must never throw from error handling.
 * @param {Object} deps - injected dependencies ({ win, doc, $ })
 * @returns {void}
 */
function stopSpinner(deps) {
    try {
        if (deps && typeof deps.$ === "function" && typeof deps.$.spinner === "function") {
            const spinner = deps.$.spinner();
            if (spinner && typeof spinner.stop === "function") {
                spinner.stop();
            }
        }
    } catch (error) {
        // Cleanup must never throw out of an error-handling path.
    }
}

/**
 * Wraps a wallet instance's onSubmit so the sentinel thrown by the wrapped callPaymentFromComponent
 * can never escape as an unhandled rejection.
 *
 * Apple's onSubmit (applepay.js ~183) has no try/catch of its own, so without this wrapper the
 * sentinel would propagate straight out of wallet.onSubmit(...) uncaught. Google's onSubmit
 * (googlepay.js ~266) does have its own try/catch and already calls
 * GooglePay.handleError(actions.reject) on any thrown error — an acceptable outcome that happens
 * before any form creation — so in practice this wrapper's catch is Apple's path; it stays in place
 * as a safety net for both regardless.
 *
 * Wrapped on the instance for the same reason as callPaymentFromComponent above and
 * walletButtonOptions.applyButtonStyling's getConfig wrapper: onSubmit is an instance arrow property
 * on both vendor classes, so reassigning it on the instance is enough and neither class is touched.
 *
 * @param {Object} wallet - vendor wallet instance
 * @param {Object} deps - injected dependencies
 * @returns {void}
 */
function wrapOnSubmit(wallet, deps) {
    if (typeof wallet.onSubmit !== "function") {
        return;
    }
    const original = wallet.onSubmit.bind(wallet);
    wallet.onSubmit = async function onSubmitWithPreflight(state, component, actions) {
        try {
            return await original(state, component, actions);
        } catch (error) {
            if (!error || error[SD_EXPRESS_HANDLED_FLAG] !== true) {
                throw error;
            }
            // The sheet is still open here — the vendor never got as far as resolving or rejecting
            // it — so reject() closes it cleanly instead of leaving it hanging open.
            if (actions && typeof actions.reject === "function") {
                actions.reject();
            }
            stopSpinner(deps);
            return undefined;
        }
    };
}

/**
 * Wraps a wallet instance so preflight rejections are handled before the vendor sees them.
 *
 * @param {Object} wallet - vendor wallet instance
 * @param {Object} deps - injected dependencies
 * @param {string} surface - "cart", "minicart" or "pdp"
 * @returns {Object} the same wallet instance
 */
function wrapWallet(wallet, deps, surface) {
    if (!wallet || typeof wallet.callPaymentFromComponent !== "function") {
        return wallet;
    }
    const original = wallet.callPaymentFromComponent.bind(wallet);
    wallet.callPaymentFromComponent = async function callPaymentFromComponentWithPreflight(data) {
        const response = await original(data);
        const handled = handleCartRejection(deps, surface, response);

        if (handled && response && response.expressAddressError === true) {
            // Thrown, not returned — see the module doc for why: returning would hand the response
            // straight into the vendor's onSubmit continuation, which is exactly what must not run.
            const sentinel = new Error("Superdry: express address preflight rejection already handled");
            sentinel[SD_EXPRESS_HANDLED_FLAG] = true;
            throw sentinel;
        }

        if (!handled) {
            // Retry insurance. On a first attempt the confirmation form does not exist yet and this
            // is a no-op — the token is applied when the form is created. On a retry within the same
            // page the form already exists, so the value is refreshed here before the wallet
            // submits it. Idempotent either way.
            confirmationForm.ensureCsrfToken(deps.doc);
        }

        // Plain DGF-3054 cartError (handled === true, expressAddressError !== true): the navigation
        // above already makes the vendor's onSubmit continuation moot, so the response is still just
        // returned here, exactly as before this change.
        return response;
    };
    wrapOnSubmit(wallet, deps);
    return wallet;
}

module.exports = {
    isCartRejection: isCartRejection,
    handleCartRejection: handleCartRejection,
    wrapWallet: wrapWallet
};
